import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthenticatedLayout } from '../layouts/AuthenticatedLayout';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { FormInput } from '../components/common/FormInput';
import { SelectInput } from '../components/common/SelectInput';
import { createTransaction } from '../services/transactions';
import { getCategories } from '../services/categories';

export const AddTransactionPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    amount: '',
    category: '',
    type: 'expense',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getCategories();
        const categoryList = Array.isArray(data) ? data : (data.categories || data.results || []);
        setCategories(categoryList);
      } catch (error) {
        setCategories([]);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!formData.description) newErrors.description = 'Description is required';
    if (!formData.amount) newErrors.amount = 'Amount is required';
    if (!formData.category) newErrors.category = 'Category is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      
      await createTransaction(formData);
      navigate('/transactions');
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthenticatedLayout>
      <div className="w-full max-w-2xl px-4 sm:px-6 lg:px-0">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6">Add New Transaction</h1>

        <Card>
          <form onSubmit={handleSubmit} className="space-y-4">
            <FormInput
              label="Date"
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
            />

            <FormInput
              label="Description"
              type="text"
              name="description"
              placeholder="Enter transaction description"
              value={formData.description}
              onChange={handleChange}
              error={errors.description}
            />

            <FormInput
              label="Amount"
              type="number"
              name="amount"
              placeholder="0.00"
              value={formData.amount}
              onChange={handleChange}
              error={errors.amount}
            />

            <SelectInput
              label="Category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              error={errors.category}
              disabled={categoriesLoading}
              options={categories.map((cat) => ({
                label: cat.name,
                value: cat.id,
              }))}
            />

            <SelectInput
              label="Type"
              name="type"
              value={formData.type}
              onChange={handleChange}
              options={[
                { label: 'Income', value: 'income' },
                { label: 'Expense', value: 'expense' },
              ]}
            />

            {errors.submit && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
                {errors.submit}
              </div>
            )}

            <div className="flex gap-4 pt-4">
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? 'Adding...' : 'Add Transaction'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/transactions')}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </AuthenticatedLayout>
  );
};
